'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { Menu, X, BookOpen } from 'lucide-react'
import { AppShell } from '@/components/layout/AppShell'
import { MarketingChrome, BRAND } from '@/components/marketing/MarketingChrome'
import { useActiveHeading } from '@/components/marketing/Editorial'

const TOC = [
  { id: 'getting-started', n: '01', label: 'Getting started' },
  { id: 'templates', n: '02', label: 'Templates & editor' },
  { id: 'multi-page', n: '03', label: 'Multi-page templates' },
  { id: 'text-layers', n: '04', label: 'Text & curved text' },
  { id: 'image-layers', n: '05', label: 'Image layers' },
  { id: 'free-roam', n: '06', label: 'Free Roam Zone' },
  { id: 'conditional-layers', n: '07', label: 'Conditional layers' },
  { id: 'layer-groups', n: '08', label: 'Layer groups' },
  { id: 'fonts', n: '09', label: 'Fonts' },
  { id: 'media', n: '10', label: 'Media library' },
  { id: 'background-removal', n: '11', label: 'Background removal' },
  { id: 'products', n: '12', label: 'Products' },
  { id: 'storefront', n: '13', label: 'Storefront widget & themes' },
  { id: 'orders', n: '14', label: 'Orders & artwork' },
  { id: 'billing', n: '15', label: 'Plans & billing' },
  { id: 'faq', n: '16', label: 'FAQ' },
]

export default function DocsShell({ children }: { children: React.ReactNode }) {
  const [{ inIframe, mounted }, setEnvState] = useState({ inIframe: false, mounted: false })
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const active = useActiveHeading(TOC.map((s) => s.id))
  const activeEntry = TOC.find((s) => s.id === active) ?? TOC[0]

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time client-side detection, no cascade risk
    setEnvState({ inIframe: window.self !== window.top, mounted: true })
  }, [])

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    if (!mobileMenuOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [mobileMenuOpen])

  // Close drawer on Escape
  useEffect(() => {
    if (!mobileMenuOpen) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMobileMenuOpen(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [mobileMenuOpen])

  if (mounted && inIframe) {
    return (
      <AppShell>
        <div className="px-6 py-6 max-w-4xl mx-auto">{children}</div>
      </AppShell>
    )
  }

  // Standalone
  return (
    <MarketingChrome active="docs">
      {/* Hero */}
      <section className="saas-hero" style={{ position: 'relative', overflow: 'hidden', borderBottom: `1px solid ${BRAND.rule}` }}>
        <div className="saas-hero-grid" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 pt-20 pb-16 md:pt-24 md:pb-20 relative">
          <div className="grid md:grid-cols-12 gap-8 items-end">
            <div className="md:col-span-8">
              <span className="saas-eyebrow">Documentation</span>
              <h1 className="font-display" style={{
                fontWeight: 700,
                fontSize: 'clamp(40px, 8vw, 80px)',
                lineHeight: 0.98,
                color: BRAND.ink,
                marginTop: 18,
              }}>
                How to use every bit of it.
              </h1>
            </div>
            <div className="md:col-span-4">
              <p style={{ fontSize: 'clamp(15px, 1.6vw, 17px)', lineHeight: 1.6, color: BRAND.muted }}>
                Install, design, assign, fulfil. Sixteen sections covering the app from first click to print-ready download.
              </p>
              <div style={{
                marginTop: 20,
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 11, letterSpacing: '0.16em',
                textTransform: 'uppercase', color: BRAND.mutedSoft,
              }}>
                ~ 15 min read
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile sticky chapter strip — visible <lg, sits below the site header */}
      <div
        className="lg:hidden"
        style={{
          position: 'sticky',
          top: 64,
          zIndex: 30,
          background: 'rgba(255,255,255,0.92)',
          backdropFilter: 'saturate(180%) blur(10px)',
          WebkitBackdropFilter: 'saturate(180%) blur(10px)',
          borderBottom: `1px solid ${BRAND.rule}`,
        }}
      >
        <button
          type="button"
          onClick={() => setMobileMenuOpen(true)}
          className="w-full flex items-center justify-between gap-3 px-5 sm:px-6"
          style={{ height: 52, color: BRAND.ink, fontFamily: 'inherit' }}
          aria-label="Open documentation menu"
          aria-haspopup="dialog"
        >
          <span className="flex items-center gap-2.5 min-w-0">
            <BookOpen size={15} strokeWidth={2.2} style={{ color: BRAND.indigo, flexShrink: 0 }} />
            <span style={{
              fontFamily: "'IBM Plex Sans', sans-serif",
              fontSize: 11,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: BRAND.mutedSoft,
              flexShrink: 0,
            }}>
              §{activeEntry.n}
            </span>
            <span className="truncate" style={{
              fontSize: 14,
              fontWeight: 600,
              letterSpacing: '-0.015em',
              color: BRAND.ink,
            }}>
              {activeEntry.label}
            </span>
          </span>
          <span
            className="flex items-center gap-1.5 shrink-0"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: BRAND.indigo,
            }}
          >
            <Menu size={15} strokeWidth={2.2} />
            Menu
          </span>
        </button>
      </div>

      {/* Mobile drawer — full TOC */}
      {mobileMenuOpen && (
        <div className="lg:hidden" role="dialog" aria-modal="true" aria-label="Documentation menu">
          <div
            onClick={() => setMobileMenuOpen(false)}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(20,20,20,0.45)',
              zIndex: 60,
            }}
          />
          <div
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: 'min(86vw, 360px)',
              background: '#fff',
              zIndex: 61,
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '-20px 0 60px -20px rgba(0,0,0,0.25)',
            }}
          >
            <div
              className="flex items-center justify-between px-5"
              style={{
                height: 60,
                borderBottom: `1px solid ${BRAND.rule}`,
              }}
            >
              <span style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 11,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: BRAND.mutedSoft,
                fontWeight: 600,
              }}>
                On this page
              </span>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                aria-label="Close menu"
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  background: BRAND.gray100,
                  color: BRAND.ink,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                }}
              >
                <X size={16} strokeWidth={2.2} />
              </button>
            </div>
            <ol
              className="overflow-y-auto"
              style={{ flex: 1, padding: '8px 0' }}
            >
              {TOC.map((s) => {
                const isActive = active === s.id
                return (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-baseline gap-3 px-5"
                      style={{
                        padding: '12px 20px',
                        color: isActive ? BRAND.indigo : BRAND.ink,
                        fontWeight: isActive ? 600 : 500,
                        fontSize: 14.5,
                        background: isActive ? 'rgba(67, 87, 173, 0.06)' : 'transparent',
                        borderLeft: `3px solid ${isActive ? BRAND.indigo : 'transparent'}`,
                      }}
                    >
                      <span style={{
                        fontFamily: "'IBM Plex Sans', sans-serif",
                        fontSize: 11,
                        letterSpacing: '0.1em',
                        color: isActive ? BRAND.indigo : BRAND.mutedSoft,
                        width: 22,
                        flexShrink: 0,
                      }}>
                        {s.n}
                      </span>
                      <span style={{ letterSpacing: '-0.015em' }}>{s.label}</span>
                    </a>
                  </li>
                )
              })}
            </ol>
            <div
              className="px-5 py-4"
              style={{ borderTop: `1px solid ${BRAND.rule}`, background: BRAND.gray50 }}
            >
              <Link
                href="/support"
                onClick={() => setMobileMenuOpen(false)}
                style={{ fontSize: 14, fontWeight: 600, color: BRAND.ink }}
              >
                Need help? <span style={{ color: BRAND.indigo }}>Open a ticket →</span>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Body: TOC + content */}
      <div style={{ background: '#fff' }}>
        <div className="max-w-[1200px] mx-auto px-5 sm:px-6 py-14 md:py-20 grid lg:grid-cols-12 gap-10">

          {/* Side TOC */}
          <aside className="lg:col-span-3 hidden lg:block">
            <div style={{ position: 'sticky', top: 88 }}>
              <div style={{
                fontFamily: "'IBM Plex Sans', sans-serif",
                fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase',
                color: BRAND.mutedSoft, marginBottom: 16,
              }}>
                On this page
              </div>
              <ol style={{ borderTop: `1px solid ${BRAND.rule}` }}>
                {TOC.map((s) => {
                  const isActive = active === s.id
                  return (
                    <li key={s.id} style={{ borderBottom: `1px solid ${BRAND.rule}` }}>
                      <a
                        href={`#${s.id}`}
                        className="flex items-baseline gap-3 py-2.5 group"
                        style={{
                          color: isActive ? BRAND.indigo : BRAND.ink,
                          fontWeight: isActive ? 600 : 500,
                          fontSize: 13,
                          transition: 'color 0.2s',
                          position: 'relative',
                        }}
                      >
                        <span className="tabular" style={{
                          fontFamily: "'IBM Plex Sans', sans-serif",
                          fontSize: 10, letterSpacing: '0.1em',
                          color: isActive ? BRAND.indigo : BRAND.mutedSoft,
                          width: 22, flexShrink: 0,
                        }}>
                          {s.n}
                        </span>
                        <span style={{ letterSpacing: '-0.015em' }}>{s.label}</span>
                        {isActive && (
                          <span style={{ position: 'absolute', left: -10, top: '50%', transform: 'translateY(-50%)', width: 4, height: 18, background: BRAND.indigo }} />
                        )}
                      </a>
                    </li>
                  )
                })}
              </ol>

              <div className="mt-8 pt-6" style={{ borderTop: `1px solid ${BRAND.rule}` }}>
                <div style={{
                  fontFamily: "'IBM Plex Sans', sans-serif",
                  fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase',
                  color: BRAND.mutedSoft, marginBottom: 12,
                }}>Need help?</div>
                <Link href="/support" style={{ fontSize: 13.5, color: BRAND.ink, fontWeight: 600 }}>
                  Open a support ticket <span style={{ color: BRAND.indigo }}>→</span>
                </Link>
              </div>
            </div>
          </aside>

          {/* Content — the surrounding <main> comes from MarketingChrome */}
          <div className="lg:col-span-9 min-w-0">
            <article className="docs-article">
              {children}
            </article>

            {/* Tail CTA */}
            <div
              style={{
                marginTop: 80,
                background: BRAND.ink,
                color: '#fff',
                padding: 'clamp(32px, 5vw, 48px)',
                borderRadius: 20,
              }}
            >
              <span className="saas-eyebrow" style={{ color: BRAND.gold }}>Need a hand?</span>
              <h3 className="font-display" style={{
                fontWeight: 700,
                fontSize: 'clamp(28px, 3.5vw, 40px)',
                lineHeight: 1.05,
                color: '#fff',
                marginTop: 14, marginBottom: 14,
              }}>
                Still missing something?
              </h3>
              <p style={{ fontSize: 15, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, marginBottom: 28, maxWidth: 520 }}>
                The manual can&apos;t cover everything. Open a ticket — we respond within one business day, in plain English, with the specific answer to your specific question.
              </p>
              <Link href="/support" className="btn-coral">
                Open a ticket
                <span style={{ marginLeft: 4 }}>→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </MarketingChrome>
  )
}
